import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';

import { graphSettingsRouteConfig } from 'src/app/graph/routes';
import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useMarkdownTransforms } from 'src/hooks/useMarkdownTransforms';
import { useRouteParams } from 'src/hooks/useRouteParams';
import {
  StudioEditorContainer,
  useStudioEditor,
} from 'src/hooks/useStudioEditor';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { SafeMarkdown } from 'src/lib/safeCompileMarkdown/SafeMarkdown';

const validationSchema = Yup.object().shape({
  template: Yup.string(),
});

type FormValues = Yup.InferType<typeof validationSchema>;

export const DescriptionTemplateModal = ({
  isOpen,
  onClose,
  graph,
}: {
  isOpen: boolean;
  onClose: () => void;
  graph: GraphQLTypes.ProposalDescriptionTemplateCardFragment;
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const { graphId } = useRouteParams(graphSettingsRouteConfig);

  const [updateDescriptionTemplate] = useMutation<
    GraphQLTypes.ProposalDescriptionTemplateModalMutation,
    GraphQLTypes.ProposalDescriptionTemplateModalMutationVariables
  >(gql`
    mutation ProposalDescriptionTemplateModalMutation(
      $graphId: ID!
      $input: SetProposalDescriptionTemplateInput!
    ) {
      service(id: $graphId) {
        setProposalDescriptionTemplate(input: $input) {
          ... on Service {
            id
            proposalDescriptionTemplate
          }
          ... on PermissionError {
            message
          }
          ... on ValidationError {
            message
          }
        }
      }
    }
  `);

  const {
    values,
    isSubmitting,
    isValid,
    setSubmitting,
    setFieldValue,
    handleSubmit,
    resetForm,
  } = useFormik<FormValues>({
    validateOnChange: true,
    initialValues: {
      template: graph.proposalDescriptionTemplate || '',
    },
    onSubmit: async ({ template }) => {
      setSubmitting(true);
      const { data: responseData } = await updateDescriptionTemplate({
        variables: {
          graphId,
          input: {
            descriptionTemplate: template,
          },
        },
        update: (cache) => {
          cache.evict({
            id: cache.identify({
              __typename: 'Service',
              id: graphId,
            }),
            fieldName: 'proposalDescriptionTemplate',
          });
        },
      });
      if (
        responseData?.service?.setProposalDescriptionTemplate?.__typename ===
        'Service'
      ) {
        onClose();
        resetForm({});
        setSubmitting(false);
        return;
      }
      setSubmitting(false);

      if (
        responseData?.service?.setProposalDescriptionTemplate?.__typename ===
          'PermissionError' ||
        responseData?.service?.setProposalDescriptionTemplate?.__typename ===
          'ValidationError' ||
        !responseData?.service?.setProposalDescriptionTemplate
      ) {
        setErrorMessage(
          responseData?.service?.setProposalDescriptionTemplate?.message ??
            'An error has occurred',
        );
      } else {
        setErrorMessage(
          assertUnreachableOrReturnDefault(
            responseData.service.setProposalDescriptionTemplate,
            'An error has occurred',
          ),
        );
      }
    },
    validationSchema,
  });

  const [tab, setTab] = useState<'markdown' | 'preview'>('markdown');

  const handleClose = useCallback(() => {
    onClose();
    resetForm({});
  }, [onClose, resetForm]);

  const markdownEditor = useStudioEditor({
    language: 'markdown',
    defaultValue: values.template,
    glyphMargin: false,
  });

  useMarkdownTransforms({ editor: markdownEditor });

  return (
    <Modal size="xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent className="flex h-[80vh] flex-col">
        <ModalHeader className="flex-row bg-silver-light p-0">
          <HeaderTab
            className="ml-6 text-sm"
            tabID="Markdown"
            isActive={() => tab === 'markdown'}
            onClick={() => {
              setTab('markdown');
            }}
          />
          <HeaderTab
            className="ml-6 text-sm"
            tabID="Preview"
            isActive={() => tab === 'preview'}
            onClick={() => {
              setTab('preview');
            }}
          />
        </ModalHeader>
        <ModalBody className="flex h-0 flex-1 border-y border-primary">
          {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          {tab === 'markdown' ? (
            <StudioEditorContainer editor={markdownEditor} className="h-full" />
          ) : (
            <SafeMarkdown
              className="overflow-auto"
              markdown={markdownEditor.getValue()}
              includeDefaultOverrides={true}
              shouldPopulateEmojis={true}
              analyticsLabelPrefix="Proposal Description Template content"
              analyticsCategory="Graph Settings Variants"
            />
          )}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              loadingText="Save template"
              onClick={() => {
                setFieldValue('template', markdownEditor.getValue());
                handleSubmit();
              }}
            >
              Save template
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
